import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";

const ENV_BACKEND = process.env.REACT_APP_BACKEND_URL;
const bare = (h) => h.replace(/^www\./, "");
// Same site reached via www/apex: call the page's own origin so a domain redirect can't break CORS.
export const BACKEND_URL = bare(new URL(ENV_BACKEND).hostname) === bare(window.location.hostname) ? window.location.origin : ENV_BACKEND;
export const api = axios.create({ baseURL: `${BACKEND_URL}/api` });
let tokenGetter = null;
export const setTokenGetter = (fn) => { tokenGetter = fn; };
api.interceptors.request.use(async (config) => {
  const token = tokenGetter ? await tokenGetter() : null;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const errMsg = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => `${x.loc?.slice(-1)[0] || ""} ${x.msg}`.trim()).join("; ");
  return fallback;
};
export const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: Number(n || 0) % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
export const today = () => new Date().toLocaleDateString("en-CA");
export const newRef = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
export const label = (s) => (s || "").replaceAll("_", " ");

export function useLoad(url, initial) {
  const [data, setData] = useState(initial);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const latest = useRef(url);
  latest.current = url;
  const reload = useCallback(() => {
    if (!url) { setLoading(false); return Promise.resolve(); }
    setLoading(true);
    const fresh = () => latest.current === url;
    return api.get(url)
      .then((r) => { if (fresh()) { setData(r.data); setError(""); } })
      .catch((e) => fresh() && setError(errMsg(e, "Could not load data")))
      .finally(() => fresh() && setLoading(false));
  }, [url]);
  useEffect(() => { reload(); }, [reload]);
  return { data, setData, error, loading, reload };
}

export function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (fn) => {
    setBusy(true); setError("");
    try { return await fn(); } catch (e) { setError(errMsg(e)); return null; } finally { setBusy(false); }
  };
  return { busy, error, setError, run };
}

export const MemberContext = createContext(null);
export const useMember = () => useContext(MemberContext);
export const useCan = () => { const m = useMember(); return (perm) => !!m?.permissions?.includes(perm); };
